/**
 * Rótulos de domínio e textos de interface (pt-BR).
 *
 * Centraliza mapas de códigos RFB (situação, porte, sócios) e as strings de UI
 * reutilizadas — mantendo os textos fora do JSX e prontos para internacionali-
 * zação futura. Situações e portes chegam também pelos lookups da API; estes
 * mapas servem de fallback e definem o estilo visual das badges.
 */

/** Unidades federativas do Brasil (fallback quando o lookup falha). */
export const BRAZIL_UFS: readonly string[] = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

/** Situação cadastral (código RFB → rótulo). */
export const SITUACAO_LABELS: Record<number, string> = {
  1: "Nula",
  2: "Ativa",
  3: "Suspensa",
  4: "Inapta",
  8: "Baixada",
};

/** Classes Tailwind da badge de situação, por código. */
export const SITUACAO_BADGE_STYLES: Record<number, string> = {
  1: "bg-slate-100 text-slate-700 ring-slate-500/20",
  2: "bg-green-100 text-green-800 ring-green-600/20",
  3: "bg-amber-100 text-amber-800 ring-amber-600/20",
  4: "bg-orange-100 text-orange-800 ring-orange-600/20",
  8: "bg-red-100 text-red-800 ring-red-600/20",
};

/** Estilo padrão para situações desconhecidas. */
export const SITUACAO_BADGE_FALLBACK = "bg-slate-100 text-slate-700 ring-slate-500/20";

/** Porte da empresa (código RFB → rótulo). */
export const PORTE_LABELS: Record<number, string> = {
  0: "Não informado",
  1: "Micro empresa",
  3: "Empresa de pequeno porte",
  5: "Demais",
};

/** Matriz/filial (código RFB → rótulo). */
export const MATRIZ_FILIAL_LABELS: Record<number, string> = {
  1: "Matriz",
  2: "Filial",
};

/** Identificador de sócio (código RFB → rótulo). */
export const IDENTIFICADOR_SOCIO_LABELS: Record<number, string> = {
  1: "Pessoa jurídica",
  2: "Pessoa física",
  3: "Estrangeiro",
};

/** Faixa etária do sócio (código RFB → rótulo). */
export const FAIXA_ETARIA_LABELS: Record<number, string> = {
  0: "Não informada",
  1: "0 a 12 anos",
  2: "13 a 20 anos",
  3: "21 a 30 anos",
  4: "31 a 40 anos",
  5: "41 a 50 anos",
  6: "51 a 60 anos",
  7: "61 a 70 anos",
  8: "71 a 80 anos",
  9: "Maior de 80 anos",
};

/** Resolve um rótulo num mapa, com fallback informativo. */
export function labelFor(map: Record<number, string>, code: number | null | undefined): string {
  if (code === null || code === undefined) {
    return "—";
  }
  return map[code] ?? String(code);
}

/** Situação de um token de integração, derivada no cliente. */
export type TokenStatus = "ativo" | "expirado" | "revogado";

/** Rótulo pt-BR de cada situação de token. */
export const TOKEN_STATUS_LABELS: Record<TokenStatus, string> = {
  ativo: "Ativo",
  expirado: "Expirado",
  revogado: "Revogado",
};

/** Classes Tailwind da badge de token — mesmas bases da badge de situação. */
export const TOKEN_STATUS_BADGE_STYLES: Record<TokenStatus, string> = {
  ativo: "bg-green-100 text-green-800 ring-green-600/20",
  expirado: "bg-amber-100 text-amber-800 ring-amber-600/20",
  revogado: "bg-red-100 text-red-800 ring-red-600/20",
};

/** Textos de interface reutilizados na aplicação. */
export const UI = {
  appName: "FleetZip",
  appTagline: "Ferramenta interna de prospecção",

  actions: {
    search: "Pesquisar",
    saveShare: "Salvar ou compartilhar",
    exportCsv: "Exportar CSV",
    clear: "Limpar filtros",
    back: "Voltar",
    logout: "Sair",
    save: "Salvar",
    delete: "Excluir",
    apply: "Aplicar",
    copyLink: "Copiar link",
    close: "Fechar",
    retry: "Tentar novamente",
    add: "Adicionar",
    signIn: "Entrar",
  },

  filters: {
    socio: "Sócio",
    termo: "Razão Social ou Nome Fantasia",
    cnae: "Atividade Principal (CNAE)",
    incluirCnaeSecundaria: "Incluir Atividade Secundária",
    natureza: "Natureza Jurídica",
    situacao: "Situação Cadastral",
    uf: "Estado (UF)",
    municipio: "Município",
    bairro: "Bairro",
    cep: "CEP",
    ddd: "DDD",
    aberturaDe: "Data de Abertura — A partir de",
    aberturaAte: "Data de Abertura — Até",
    capitalDe: "Capital Social — A partir de",
    capitalAte: "Capital Social — Até",
    somenteMei: "Somente MEI",
    excluirMei: "Excluir MEI",
    somenteMatriz: "Somente matriz",
    somenteFilial: "Somente filial",
    comTelefone: "Com contato de telefone",
    somenteFixo: "Somente fixo",
    somenteCelular: "Somente celular",
    comEmail: "Com e-mail",
    porte: "Porte",
  },

  table: {
    razaoSocial: "Razão Social",
    nomeFantasia: "Nome Fantasia",
    cnpj: "CNPJ",
    situacao: "Situação",
    local: "UF / Município",
    cnae: "Atividade principal",
    abertura: "Abertura",
    capital: "Capital Social",
  },

  tokens: {
    navLabel: "Tokens de integração",
    title: "Tokens de integração",
    subtitle:
      "Credenciais de API para integrações acessarem o Eye em nome de um usuário. Restrito a administradores.",
    newToken: "Novo token",
    emptyTitle: "Nenhum token de integração",
    emptyDescription:
      "Crie um token para permitir que uma integração consuma a API do Eye programaticamente.",
    noExpiration: "Sem expiração",
    never: "Nunca",
    copyWarning: "Copie o token agora — ele não será exibido novamente.",
    copyHint: "Guarde-o num local seguro (gerenciador de segredos). O Eye só mantém o hash.",
    copy: "Copiar",
    copied: "Copiado!",
    revoke: "Revogar",
    revokeConfirmTitle: "Revogar token",
    revokeConfirmWarning:
      "Integrações que usam este token param de funcionar imediatamente. Esta ação não pode ser desfeita.",
    createTitle: "Novo token de integração",
    createdTitle: "Token criado",
    nameLabel: "Nome",
    namePlaceholder: "Ex.: Integração CRM",
    nameHint: "Identifica onde o token é usado. Máximo de 100 caracteres.",
    setExpiration: "Definir data de expiração",
    expirationLabel: "Expira em",
    create: "Criar token",
    done: "Concluir",
    columns: {
      name: "Nome",
      creator: "Criador",
      created: "Criação",
      expires: "Expiração",
      lastUsed: "Último uso",
      status: "Status",
      actions: "Ações",
    },
  },
} as const;
